import {
    Container,
    Box,
    Snackbar,
    Typography,
    Stack,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import React, { ReactElement, useState, useEffect } from 'react';
import { OrganizationPublic } from '../../../utils';
import getOrganizationBySlug from '../../../api/Public/getOrganizationBySlug';
import { useHistory } from 'react-router-dom';
import getCurrentUser from '../../../api/Auth/getCurrentUser';
import { useSelector } from 'react-redux';
import { authSelector } from 'redux/reducers/auth';
import { AxiosError } from 'axios';
import PageMeta from '../../../components/Meta/PageMeta';

const AthleteStart = (): ReactElement => {
    const [organization, setOrganization] = useState<OrganizationPublic | null>(null);
    const [snackMessage, setSnackMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showNotFoundAlert, setShowNotFoundAlert] = useState<boolean>(false);
    const history = useHistory();
    const { isAuth } = useSelector(authSelector);

    useEffect(() => {
        const getOrganizationFromUrlSlug = async () => {
            try {
                setIsLoading(true);
                setShowNotFoundAlert(false); // Reset alert state
                const response = await getOrganizationBySlug(
                    window.location.pathname.split('/')[2],
                );
                console.log(response.data);
                setOrganization(response.data);

                try {
                    if (isAuth) {
                        const userData = await getCurrentUser();
                        if (userData.data) {
                            // If user is already logged in, redirect to the athlete-subscribe page
                            history.push(`/athletes-subscribe/${response.data.slug}`);
                        }
                    }
                } catch (e) {
                    console.log('No user is currently logged in.');
                }
            } catch (e) {
                console.error('ERROR', e);
                // Check if it's an AxiosError and handle 404 or any non-2xx response
                if (e instanceof Error && 'response' in e) {
                    const axiosError = e as AxiosError;
                    if (
                        axiosError.response?.status === 404 ||
                        (axiosError.response?.status &&
                            (axiosError.response.status < 200 || axiosError.response.status >= 300))
                    ) {
                        setShowNotFoundAlert(true);
                    } else {
                        // For other errors, show the original snack message
                        setSnackMessage(
                            'Error retrieving organization information. Please check with your coach.',
                        );
                    }
                } else {
                    // For non-Axios errors, show the original snack message
                    setSnackMessage(
                        'Error retrieving organization information. Please check with your coach.',
                    );
                }
            } finally {
                setIsLoading(false);
            }
        };

        void getOrganizationFromUrlSlug();
    }, [history, isAuth]);

    const handleButtonClick = (path: string) => {
        const baseUrl = process.env.REACT_APP_URL || 'https://1tul.com';

        /**
         * This is the URL for the athlete to get a combo subscription upon login/registration.
         */
        const athleteSubscribeUrl = `${baseUrl}/athletes-subscribe/${organization?.slug}`;
        localStorage.setItem('athleteSubscribeUrl', athleteSubscribeUrl);

        /**
         * For registration, this is the organization slug which will be used to assign this
         * organization's partner to this athlete in dim_user.assigned_partner_key.
         */
        if (organization?.slug) {
            localStorage.setItem('organizationSlug', organization.slug);
        }

        history.push(path);
    };

    return (
        <main>
            <PageMeta pageType="athlete-start" organization={organization} />
            <Snackbar
                open={snackMessage !== ''}
                autoHideDuration={6000}
                onClose={() => setSnackMessage('')}
            >
                <Stack
                    sx={{ width: '100%' }}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                >
                    <Typography>{snackMessage}</Typography>
                </Stack>
            </Snackbar>

            {/* Alert for organization not found */}
            {showNotFoundAlert && (
                <Container sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Alert severity="error" sx={{ maxWidth: '600px', width: '100%' }}>
                        Could not find an organization from this link. Please check with your coach.
                    </Alert>
                </Container>
            )}

            {/* Only show organization header and logo if organization loaded successfully */}
            {!showNotFoundAlert && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: 8,
                        pb: 6,
                    }}
                >
                    <Typography
                        align="center"
                        color="textPrimary"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.5rem',
                        }}
                    >
                        {organization?.name} account setup
                    </Typography>
                    <Box
                        sx={{
                            width: 150,
                            height: 150,
                            borderRadius: '25%',
                            border: '2px solid',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            mb: 2,
                        }}
                    >
                        <img
                            src={organization?.logo_url || ''}
                            alt={`${organization?.name} logo`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                </Box>
            )}
            {!isLoading && organization ? (
                <Container>
                    <Typography variant="h6" align="center" gutterBottom>
                        Do you have a 1Tul account?
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => handleButtonClick('/login')}
                            style={{ marginRight: 10, width: '100px' }}
                        >
                            YES
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => handleButtonClick('/register')}
                            style={{ width: '100px' }}
                        >
                            NO
                        </Button>
                    </Stack>
                </Container>
            ) : !showNotFoundAlert ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : null}
        </main>
    );
};

export default AthleteStart;
